import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';
import { useAuth } from '@/lib/auth/AuthContext';
import type { PageDto } from '@/lib/pagination';

export interface TemplateOut {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  system_prompt: string | null;
  instructions: string;
  json_schema: Record<string, unknown>;
  category: string;
  is_active: boolean;
  enable_tagging: boolean;
  tagging_instructions: string | null;
  created_at: string;
  updated_at: string;
}

export type PaginatedTemplates = PageDto<TemplateOut>;

export interface TemplateCreateData {
  name: string;
  description?: string;
  system_prompt?: string;
  instructions: string;
  json_schema: Record<string, unknown>;
  category?: string;
  is_active?: boolean;
  enable_tagging?: boolean;
  tagging_instructions?: string | null;
}

export interface TemplateUpdateData {
  name?: string;
  description?: string | null;
  system_prompt?: string | null;
  instructions?: string;
  json_schema?: Record<string, unknown>;
  is_active?: boolean;
  enable_tagging?: boolean;
  tagging_instructions?: string | null;
}

function orgUrl(orgId: string) {
  return `/api/v1/organizations/${orgId}/templates`;
}

export function useTemplates(params?: { limit?: number; offset?: number; search?: string; active_only?: boolean; category?: string }) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    queryKey: ['templates', orgId, params],
    queryFn: async () => {
      const { data } = await clientApi.get<TemplateOut[]>(orgUrl(orgId!), { params });
      const items = Array.isArray(data) ? data : (data as unknown as PaginatedTemplates).data;
      return {
        data: items,
        meta: { page: 1, limit: items.length, total_items: items.length, total_pages: 1, has_previous_page: false, has_next_page: false },
      } satisfies PaginatedTemplates;
    },
    enabled: !!orgId,
  });
}

export function useTemplate(templateId: string | undefined) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    queryKey: ['templates', orgId, templateId],
    queryFn: async () => {
      const { data } = await clientApi.get<TemplateOut>(`${orgUrl(orgId!)}/${templateId}`);
      return data;
    },
    enabled: !!orgId && !!templateId,
  });
}

export function useCreateTemplate() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: TemplateCreateData) => {
      const { data } = await clientApi.post<TemplateOut>(orgUrl(orgId!), body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates', orgId] });
    },
  });
}

export function useUpdateTemplate() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...body }: TemplateUpdateData & { id: string }) => {
      const { data } = await clientApi.patch<TemplateOut>(`${orgUrl(orgId!)}/${id}`, body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates', orgId] });
    },
  });
}

export function useDeleteTemplate() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (templateId: string) => {
      await clientApi.delete(`${orgUrl(orgId!)}/${templateId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates', orgId] });
    },
  });
}
